// src/features/seec/components/SeecReports.jsx

import React, { useEffect, useId, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  doc,
  setDoc,
  where,
} from "firebase/firestore";
import UnifiedContributionSection from "../sections/UnifiedContributionSection";
import UnifiedExpenditureSection from "../sections/UnifiedExpenditureSection";
import UnifiedSummaryReportSection from "../sections/UnifiedSummaryReportSection";
import { CommonTable } from "../../../shared/ui/table/CommonTable";
import {
  getSeecLawById,
  SEEC_CANDIDATE_NAME_TO_CAMPAIGNS,
} from "../../../shared/constants";
import { db } from "../../../firebase";
import seecExceptionUtils from "../utils/seecExceptionReportUtils";
import { SeecPageHeader } from "./SeecPageHeader";
import { SeecExplorerControls } from "./SeecExplorerControls";

const { buildSeecExceptionReport } = seecExceptionUtils;

function slugifyTownName(value) {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeDonorName(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildDonorLookupMap(donorDocs) {
  const donorLookup = new Map();

  for (const donorDoc of donorDocs) {
    const names = [
      donorDoc.displayName,
      ...(donorDoc.knownNames || []).map((entry) => entry.displayName),
    ];

    for (const name of names) {
      const key = normalizeDonorName(name);
      if (!key || donorLookup.has(key)) continue;
      donorLookup.set(key, donorDoc.id);
    }
  }

  return donorLookup;
}

function normalizeContributionRow(
  row,
  party,
  committeeName = null,
  donorLookup = new Map(),
) {
  const address =
    [row.streetAddress, row.townName || row.city, row.state]
      .filter(Boolean)
      .join(", ") || "—";
  const donorName = row.contributorName?.trim() || null;
  const donorDocId = donorName
    ? donorLookup.get(normalizeDonorName(donorName)) || null
    : null;

  return {
    "Full Name":
      donorName ||
      row.receiptType?.trim() ||
      "—",
    Address: address,
    Location: address,
    Date: row.transactionDateIso ?? row.transactionDate ?? null,
    "Contribution Amount": row.amount ?? row.amountOriginal ?? 0,
    Description: row.receiptType ?? null,
    Use: row.methodOfPayment ?? null,
    Occupation: row.occupation ?? null,
    Employer: row.employer ?? null,
    Committee: committeeName ?? row.committeeName ?? null,
    party,
    "Party Affiliation": party,
    DonorDocId: donorDocId,
  };
}

function normalizeExpenditureRow(row, party, committeeName = null) {
  return {
    "Vendor / Recipient": row.payeeName ?? "—",
    Date: row.dateOfPaymentIso ?? row.dateOfPayment ?? null,
    Amount: row.amount ?? row.amountOriginal ?? 0,
    Use: row.purposeOfExpenditure ?? "Unspecified",
    "Purpose of Expenditure": row.purposeOfExpenditure ?? null,
    "Detailed Purpose": row.description ?? null,
    streetAddress: row.streetAddress ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    __rawExpenditure: row,
    Committee: committeeName ?? row.committeeName ?? null,
    party,
    "Party Affiliation": party,
  };
}

function normalizeSummaryRow(row, party, committeeName = null) {
  return {
    Committee: row.committee ?? "—",
    "Committee Display Name": committeeName || row.committee || "—",
    "Committee Type": row.committeeType ?? "—",
    "Party Affiliation": row.partyAffiliation ?? party,
    FilingEventType: row.filingEventType ?? "—",
    Status: row.status ?? "—",
    DS: row.dataSource ?? "—",
    "R-FE": row.refiledElectronically ?? "—",
    "Disclosure Submitted Date": row.disclosureSubmittedDateIso ?? row.disclosureSubmittedDate ?? null,
    "Period Cov. Begin Date": row.periodCovBeginDateIso ?? row.periodCovBeginDate ?? null,
    "Period Cov. End Date": row.periodCovEndDateIso ?? row.periodCovEndDate ?? null,
    "Balance on hand at the beginning of Reproting Period":
      row.balanceOnHandAtBeginningOfReportingPeriod ?? 0,
    "Total Monetary Receipts": row.totalMonetaryReceipts ?? 0,
    "Expenses Paid by Committee": row.expensesPaidByCommittee ?? 0,
    "Balance on hand at close of Reporting Period":
      row.balanceOnHandAtCloseOfReportingPeriod ?? 0,
    "Beginning Loan Balance": row.beginningLoanBalance ?? 0,
    party,
  };
}

function getCommitteeDisplayName(committee) {
  return (
    committee.committeeName ||
    committee.organizationName ||
    committee.name ||
    committee.party ||
    "Unknown Organization"
  );
}

function getCommitteePriority(committee, townName) {
  const displayName = getCommitteeDisplayName(committee).toLowerCase();
  const normalizedTown = String(townName || "").toLowerCase();

  if (displayName === `${normalizedTown} democratic town committee`) {
    return 0;
  }

  if (displayName === `${normalizedTown} republican town committee`) {
    return 1;
  }

  return 2;
}

function buildCommitteeViewModel(committeeDocs, options = {}) {
  const { sortMode = "town" } = options;
  const committeeMap = new Map();
  const donorLookup = buildDonorLookupMap(committeeDocs.donors || []);

  const ensureCommittee = (organizationName, townName, party) => {
    const key = organizationName || `${townName || "unknown"}-${party || "unknown"}`;
    if (!committeeMap.has(key)) {
        committeeMap.set(key, {
          organizationName,
          committeeName: organizationName,
          party,
          townName,
          contributions: [],
          expenditures: [],
          summaries: [],
          rawReceiptDocs: [],
          rawDisbursementDocs: [],
          rawSummaryDocs: [],
        });
    }

    const committee = committeeMap.get(key);
    if (!committee.party && party) committee.party = party;
    return committee;
  };

  for (const receiptDoc of committeeDocs.receipts) {
    const party =
      receiptDoc.data.find((row) => row.committeePartyAffiliation)?.committeePartyAffiliation ||
      null;
    const committee = ensureCommittee(
      receiptDoc.organizationName,
      receiptDoc.townName,
      party
    );
    committee.contributions.push(
      ...receiptDoc.data.map((row) =>
        normalizeContributionRow(
          row,
          committee.party,
          committee.organizationName,
          donorLookup,
        )
      )
    );
    committee.rawReceiptDocs.push(receiptDoc);
  }

  for (const disbursementDoc of committeeDocs.disbursements) {
    const party =
      disbursementDoc.data.find((row) => row.committeePartyAffiliation)?.committeePartyAffiliation ||
      null;
    const committee = ensureCommittee(
      disbursementDoc.organizationName,
      disbursementDoc.townName,
      party
    );
    committee.expenditures.push(
      ...disbursementDoc.data.map((row) =>
        normalizeExpenditureRow(row, committee.party, committee.organizationName)
      )
    );
    committee.rawDisbursementDocs.push(disbursementDoc);
  }

  for (const summaryDoc of committeeDocs.summaries) {
    const party =
      summaryDoc.data.find((row) => row.partyAffiliation)?.partyAffiliation || null;
    const committee = ensureCommittee(
      summaryDoc.organizationName,
      summaryDoc.townName,
      party
    );
    committee.summaries.push(
      ...summaryDoc.data.map((row) =>
        normalizeSummaryRow(row, committee.party, committee.organizationName)
      )
    );
    committee.rawSummaryDocs.push(summaryDoc);
  }

  for (const exceptionReport of committeeDocs.exceptionReports) {
    const committee = ensureCommittee(
      exceptionReport.organizationName || exceptionReport.committeeName,
      exceptionReport.townName,
      exceptionReport.partyAffiliation || null
    );
    committee.exceptionReport = exceptionReport;
  }

  const committees = Array.from(committeeMap.values())
    .map((committee) => ({
      ...committee,
      displayName: getCommitteeDisplayName(committee),
    }))
    .sort((a, b) => {
      if (sortMode === "town") {
        const priorityDiff =
          getCommitteePriority(a, a.townName) - getCommitteePriority(b, b.townName);

        if (priorityDiff !== 0) return priorityDiff;
      }

      return a.displayName.localeCompare(b.displayName);
    });

  const allCommitteeSummaries = committees.flatMap((committee) => committee.summaries);
  const allCommitteeContributions = committees.flatMap(
    (committee) => committee.contributions,
  );

  committees.forEach((committee) => {
    committee.__allTownSummaries = allCommitteeSummaries;
    committee.__allTownContributions = allCommitteeContributions;
  });

  return {
    committees,
  };
}

function chunkValues(values, size = 10) {
  const chunks = [];

  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }

  return chunks;
}

async function fetchCollectionDocsByValues(
  collectionName,
  fieldName,
  operator,
  values,
) {
  const normalizedValues = Array.from(
    new Set((values || []).filter((value) => value !== null && value !== undefined && value !== "")),
  );

  if (!normalizedValues.length) {
    return [];
  }

  const chunks = chunkValues(normalizedValues, 10);
  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(
        query(collection(db, collectionName), where(fieldName, operator, chunk)),
      ),
    ),
  );

  const docsById = new Map();

  for (const snap of snapshots) {
    for (const docSnap of snap.docs) {
      if (!docsById.has(docSnap.id)) {
        docsById.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data(),
        });
      }
    }
  }

  return Array.from(docsById.values());
}

function CollapsibleSection({
  title,
  collapsed,
  toggle,
  children,
  className = "",
  reportId,
  reportTitle,
}) {
  const contentId = useId();
  return (
    <div id={reportId} data-report-title={reportTitle} className={`bg-white shadow-sm rounded-lg border border-gray-300 overflow-hidden ${className}`}>
      <button type="button" aria-expanded={!collapsed} aria-controls={contentId}
        className="w-full bg-gray-100 px-4 py-3 font-semibold text-lg text-gray-900 flex gap-3 justify-between items-center text-left hover:bg-gray-200 rounded-none"
        onClick={toggle}>
        {title}
        <span aria-hidden="true">{collapsed ? "▸" : "▾"}</span>
      </button>
      <div id={contentId} hidden={collapsed} className="bg-white p-3 sm:p-4">{children}</div>
    </div>
  );
}

function NoDataState({ title, message }) {
  return (
    <div className="bg-base-100 shadow-md rounded-lg p-6 border border-gray-300">
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );
}

function SeecLandingPage() {
  return (
    <section aria-label="About campaign finance reports" className="rounded-lg border border-gray-200 bg-gray-50 p-5">
      <h2 className="text-lg font-semibold text-gray-900">Follow the money</h2>
      <p className="mt-2 text-sm text-gray-600">Choose a town, campaign committee, or politician above. Each committee has three filing views:</p>
      <dl className="mt-4 grid gap-4 sm:grid-cols-3">
        {[
          ["Receipts", "Who contributed, how much, and when."],
          ["Expenditures", "Who received payments and what they were for."],
          ["Reports", "Filed summaries, reporting periods, and balances."],
        ].map(([title, description]) => (
          <div key={title}><dt className="text-sm font-semibold text-gray-900">{title}</dt><dd className="mt-1 text-sm text-gray-600">{description}</dd></div>
        ))}
      </dl>
      <p className="mt-4 border-t border-gray-200 pt-4 text-sm text-gray-600">Use Top donors to compare contributors across campaigns. A politician view combines their mapped campaigns; a candidate committee view opens one campaign.</p>
    </section>
  );
}

function NormalizedCommitteeGroup({
  committee,
  expandedSections,
  toggleSection,
  onExceptionReportSaved,
}) {
  const groupKey = `committee-${slugifyTownName(committee.displayName)}`;
  const color =
    committee.party === "Republican" ? "bg-[#fee2e2]" : "bg-[#e0f2fe]";
  const exceptionReport = committee.exceptionReport || null;
  const exceptionRows = (exceptionReport?.findings || []).map((finding, index) => {
    const law = getSeecLawById(finding.lawId);
    return {
      id: finding.id || `${finding.lawId || "finding"}-${index}`,
      law:
        finding.lawCitation ||
        law?.citation ||
        finding.lawId ||
        "Unspecified law",
      summary:
        finding.summary ||
        finding.details ||
        "Potential filing exception detected.",
    };
  });
  const showGenerateButton = !exceptionReport;
  const showNoFindings =
    exceptionReport &&
    Array.isArray(exceptionReport.findings) &&
    exceptionReport.findings.length === 0;
  const [isGeneratingExceptionReport, setIsGeneratingExceptionReport] =
    useState(false);

  async function handleGenerateExceptionReport() {
    if (isGeneratingExceptionReport) return;

    setIsGeneratingExceptionReport(true);

    try {
      const report = buildSeecExceptionReport({
        organizationName: committee.organizationName || committee.displayName,
        townName: committee.townName,
        townSlug: slugifyTownName(committee.townName || ""),
        receiptsDocs: committee.rawReceiptDocs || [],
        disbursementDocs: committee.rawDisbursementDocs || [],
        summaryDocs: committee.rawSummaryDocs || [],
        generatedBy: "client",
        generationMethod: "deterministic",
      });

      await setDoc(
        doc(db, "SEEC_EXCEPTION_REPORTS", report._id),
        {
          ...report,
          updatedAt: new Date().toISOString(),
        },
        { merge: true },
      );

      onExceptionReportSaved?.(committee.displayName, report);
    } catch (error) {
      console.error("Failed to generate SEEC exception report:", error);
    } finally {
      setIsGeneratingExceptionReport(false);
    }
  }

  return (
    <CollapsibleSection
      reportId={groupKey}
      reportTitle={committee.displayName}
      title={<span className="min-w-0"><span className="block break-words">{committee.displayName}</span><span className="mt-1 block text-xs font-normal text-gray-500">{committee.contributions.length.toLocaleString()} receipts · {committee.expenditures.length.toLocaleString()} expenditures · {committee.summaries.length.toLocaleString()} reports</span></span>}
      collapsed={!expandedSections[groupKey]}
      toggle={() => toggleSection(groupKey)}
    >
      <div className="space-y-4">
        <div id={`${groupKey}-contributions`} data-report-title="Receipts" data-report-parent={groupKey}>
        <UnifiedContributionSection
          collapsed={!expandedSections[`${groupKey}-contributions`]}
          toggle={() => toggleSection(`${groupKey}-contributions`)}
          rows={committee.contributions}
          title="Receipts"
          color={color}
        />
        </div>

        <div id={`${groupKey}-expenditures`} data-report-title="Expenditures" data-report-parent={groupKey}>
        <UnifiedExpenditureSection
          collapsed={!expandedSections[`${groupKey}-expenditures`]}
          toggle={() => toggleSection(`${groupKey}-expenditures`)}
          rows={committee.expenditures}
          title="Expenditures"
          color={color}
        />
        </div>

        <div id={`${groupKey}-reports`} data-report-title="Reports" data-report-parent={groupKey}>
        <UnifiedSummaryReportSection
          collapsed={!expandedSections[`${groupKey}-reports`]}
          toggle={() => toggleSection(`${groupKey}-reports`)}
          rows={committee.summaries}
          contributionRows={committee.contributions}
          comparisonRows={committee.__allTownSummaries}
          comparisonContributionRows={committee.__allTownContributions}
          townName={committee.townName}
          currentCommitteeName={committee.displayName}
          title="Reports"
        />
        </div>

        <div id={`${groupKey}-exceptions`} data-report-title="Exceptions" data-report-parent={groupKey} className={`bg-base-100 shadow-md rounded-lg border border-gray-300 overflow-hidden ${color}`}>
          <button type="button" aria-expanded={Boolean(expandedSections[`${groupKey}-exceptions`])}
            className="w-full bg-gray-100 px-3 py-3 font-semibold text-lg text-gray-900 flex justify-between items-center text-left hover:bg-gray-200 rounded-none"
            onClick={() => toggleSection(`${groupKey}-exceptions`)}>
            Exceptions
            <span aria-hidden="true">{!expandedSections[`${groupKey}-exceptions`] ? "▸" : "▾"}</span>
          </button>

          {expandedSections[`${groupKey}-exceptions`] && (
            <div className="bg-white p-4">
              <div className="flex flex-col gap-3">
                {showGenerateButton && (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-sm text-gray-600">
                      No exception report has been generated for this organization yet.
                    </p>
                    <button
                      type="button"
                      onClick={handleGenerateExceptionReport}
                      disabled={isGeneratingExceptionReport}
                      className={`px-3 py-1.5 rounded border text-sm ${
                        isGeneratingExceptionReport
                          ? "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isGeneratingExceptionReport ? "Generating..." : "Generate Report"}
                    </button>
                  </div>
                )}

                {exceptionReport && (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-700">Status:</span>{" "}
                      {exceptionReport.status || "unknown"}
                      {exceptionReport.generatedAt ? (
                        <>
                          {" "}
                          · <span className="font-semibold text-gray-700">Generated:</span>{" "}
                          {new Date(exceptionReport.generatedAt).toLocaleString()}
                        </>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={handleGenerateExceptionReport}
                      disabled={isGeneratingExceptionReport}
                      className={`px-3 py-1.5 rounded border text-sm ${
                        isGeneratingExceptionReport
                          ? "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      {isGeneratingExceptionReport ? "Generating..." : "Regenerate Report"}
                    </button>
                  </div>
                )}

                {exceptionReport && (
                  <CommonTable
                    title=""
                    columns={[
                      {
                        key: "law",
                        label: "Law",
                        style: { width: "260px" },
                      },
                      {
                        key: "summary",
                        label: "Exception Summary",
                        style: { width: "700px" },
                      },
                    ]}
                    data={
                      showNoFindings
                        ? [
                            {
                              id: "no-findings",
                              law: "No exceptions found",
                              summary:
                                "A report exists for this organization, but no findings were recorded.",
                            },
                          ]
                        : exceptionRows
                    }
                    headerColor={color}
                    showAggregateTotal={false}
                    emptyMessage="No exceptions have been added yet."
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
}

export function SeecReports() {
  const context = useOutletContext();
  const {
    selectedSeecPolitician,
    selectedSeecPoliticalCommittee,
    selectedSeecTown,
    selectedSeecCandidate,
  } = context;
  const selectedPolitician = selectedSeecPolitician || "";
  const selectedPoliticalCommittee = selectedSeecPoliticalCommittee || "";
  const selectedTown = selectedSeecTown || "";
  const selectedCandidate = selectedSeecCandidate || "";
  const selectedPoliticianCampaigns = useMemo(
    () =>
      (SEEC_CANDIDATE_NAME_TO_CAMPAIGNS[selectedPolitician]?.campaigns || [])
        .map((entry) => entry?.campaign)
        .filter(Boolean),
    [selectedPolitician],
  );
  const selectedTarget =
    selectedPolitician || selectedPoliticalCommittee || selectedCandidate || selectedTown;
  const selectedTargetType = selectedPolitician
    ? "politician"
    : selectedPoliticalCommittee
      ? "political"
    : selectedCandidate
      ? "candidate"
      : "town";
  const [townData, setTownData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [expandedSections, setExpandedSections] = useState({});
  const selectedTownSlug = useMemo(
    () => slugifyTownName(selectedTown),
    [selectedTown],
  );

  function handleExceptionReportSaved(committeeDisplayName, report) {
    setTownData((prev) => {
      if (!prev?.committees) return prev;

      return {
        ...prev,
        committees: prev.committees.map((committee) =>
          committee.displayName === committeeDisplayName
            ? { ...committee, exceptionReport: report }
            : committee,
        ),
      };
    });
  }

  useEffect(() => {
    let isMounted = true;

    async function loadTownData() {
      if (!selectedTarget) {
        setTownData(null);
        setLoadError("");
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError("");
      setTownData(null);

      try {
        const committeeDocs = selectedTargetType === "politician"
          ? await Promise.all([
              fetchCollectionDocsByValues(
                "SEEC_CANDIDATE_RECEIPTS",
                "organizationName",
                "in",
                selectedPoliticianCampaigns,
              ),
              fetchCollectionDocsByValues(
                "SEEC_CANDIDATE_DISBURSEMENTS",
                "organizationName",
                "in",
                selectedPoliticianCampaigns,
              ),
              fetchCollectionDocsByValues(
                "SEEC_CANDIDATE_SUMMARIES",
                "organizationName",
                "in",
                selectedPoliticianCampaigns,
              ),
              fetchCollectionDocsByValues(
                "SEEC_EXCEPTION_REPORTS",
                "organizationName",
                "in",
                selectedPoliticianCampaigns,
              ),
            ]).then(
              ([
                receipts,
                disbursements,
                summaries,
                exceptionReports,
              ]) => ({
                receipts,
                disbursements,
                summaries,
                exceptionReports,
                donors: [],
              }),
            )
          : selectedTargetType === "political"
            ? await Promise.all([
                getDocs(
                  query(
                    collection(db, "SEEC_RECEIPTS"),
                    where("organizationName", "==", selectedPoliticalCommittee),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "SEEC_DISBURSEMENTS"),
                    where("organizationName", "==", selectedPoliticalCommittee),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "SEEC_SUMMARIES"),
                    where("organizationName", "==", selectedPoliticalCommittee),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "SEEC_EXCEPTION_REPORTS"),
                    where("organizationName", "==", selectedPoliticalCommittee),
                  ),
                ),
              ]).then(
                ([
                  receiptsSnap,
                  disbursementsSnap,
                  summariesSnap,
                  exceptionReportsSnap,
                ]) => ({
                  receipts: receiptsSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  disbursements: disbursementsSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  summaries: summariesSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  exceptionReports: exceptionReportsSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  donors: [],
                }),
              )
          : selectedTargetType === "candidate"
            ? await Promise.all([
                getDocs(
                  query(
                    collection(db, "SEEC_CANDIDATE_RECEIPTS"),
                    where("organizationName", "==", selectedCandidate),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "SEEC_CANDIDATE_DISBURSEMENTS"),
                    where("organizationName", "==", selectedCandidate),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "SEEC_CANDIDATE_SUMMARIES"),
                    where("organizationName", "==", selectedCandidate),
                  ),
                ),
                getDocs(
                  query(
                    collection(db, "SEEC_EXCEPTION_REPORTS"),
                    where("organizationName", "==", selectedCandidate),
                  ),
                ),
              ]).then(
                ([
                  receiptsSnap,
                  disbursementsSnap,
                  summariesSnap,
                  exceptionReportsSnap,
                ]) => ({
                  receipts: receiptsSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  disbursements: disbursementsSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  summaries: summariesSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  exceptionReports: exceptionReportsSnap.docs.map((snap) => ({
                    id: snap.id,
                    ...snap.data(),
                  })),
                  donors: [],
                }),
              )
            : await Promise.all([
              getDocs(
                query(
                  collection(db, "SEEC_RECEIPTS"),
                  where("townSlug", "==", selectedTownSlug)
                )
              ),
              getDocs(
                query(
                  collection(db, "SEEC_DISBURSEMENTS"),
                  where("townSlug", "==", selectedTownSlug)
                )
              ),
              getDocs(
                query(
                  collection(db, "SEEC_SUMMARIES"),
                  where("townSlug", "==", selectedTownSlug)
                )
              ),
              getDocs(
                query(
                  collection(db, "SEEC_EXCEPTION_REPORTS"),
                  where("townSlug", "==", selectedTownSlug)
                )
              ),
            ]).then(
              ([
                receiptsSnap,
                disbursementsSnap,
                summariesSnap,
                exceptionReportsSnap,
              ]) => ({
                receipts: receiptsSnap.docs.map((snap) => ({
                  id: snap.id,
                  ...snap.data(),
                })),
                disbursements: disbursementsSnap.docs.map((snap) => ({
                  id: snap.id,
                  ...snap.data(),
                })),
                summaries: summariesSnap.docs.map((snap) => ({
                  id: snap.id,
                  ...snap.data(),
                })),
                exceptionReports: exceptionReportsSnap.docs.map((snap) => ({
                  id: snap.id,
                  ...snap.data(),
                })),
                donors: [],
              }),
            );

        if (isMounted) {
          const hasAnyData =
            committeeDocs.receipts.length > 0 ||
            committeeDocs.disbursements.length > 0 ||
            committeeDocs.summaries.length > 0;
          setTownData(
            hasAnyData
              ? buildCommitteeViewModel(committeeDocs, {
                  sortMode: selectedTargetType,
                })
              : null,
          );
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message || "Unable to load SEEC data.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadTownData();

    return () => {
      isMounted = false;
    };
  }, [
    selectedPolitician,
    selectedPoliticianCampaigns,
    selectedPoliticalCommittee,
    selectedCandidate,
    selectedTarget,
    selectedTargetType,
    selectedTown,
    selectedTownSlug,
  ]);

  useEffect(() => {
    setExpandedSections({});
  }, [selectedTarget]);

  useEffect(() => {
    if (!townData?.committees || townData.committees.length !== 1) {
      return;
    }

    const onlyCommittee = townData.committees[0];
    const defaultExpandedKey = `committee-${slugifyTownName(onlyCommittee.displayName)}`;

    setExpandedSections((prev) => (
      Object.keys(prev).length > 0 ? prev : { [defaultExpandedKey]: true }
    ));
  }, [townData]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const hasNormalizedTownData = Boolean(townData);

  return (
    <div className="space-y-5">
      <SeecPageHeader title="Campaign finance" description="Explore Connecticut campaign receipts, spending, and filed reports." />
      <SeecExplorerControls context={context} />
      <section className="min-w-0" aria-label="Filing results">
        {!selectedTarget ? (
          <SeecLandingPage />
        ) : loading ? (
          <div className="bg-base-100 shadow-md rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-semibold mb-2">{selectedTarget}</h2>
            <p className="text-sm text-gray-600">Loading SEEC data...</p>
          </div>
        ) : loadError ? (
          <div className="bg-base-100 shadow-md rounded-lg p-6 border border-gray-300">
            <h2 className="text-xl font-semibold mb-2">{selectedTarget}</h2>
            <p className="text-sm text-red-600">{loadError}</p>
          </div>
        ) : hasNormalizedTownData ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900">{selectedTarget} <span className="text-sm font-normal text-gray-500">· {townData.committees.length} {townData.committees.length === 1 ? "committee" : "committees"}</span></h2>
              {townData.committees.length > 1 && <div className="flex gap-3 text-sm">
                <button type="button" className="bg-transparent text-blue-700 underline" onClick={() => setExpandedSections(Object.fromEntries(townData.committees.map((committee) => [`committee-${slugifyTownName(committee.displayName)}`, true])))}>Open committees</button>
                <button type="button" className="bg-transparent text-gray-600 underline" onClick={() => setExpandedSections({})}>Close committees</button>
              </div>}
            </div>
            {townData.committees.map((committee) => (
              <NormalizedCommitteeGroup
                key={committee.displayName}
                committee={committee}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                onExceptionReportSaved={handleExceptionReportSaved}
              />
            ))}
          </div>
        ) : (
          <NoDataState
            title={selectedTarget}
            message={`No normalized SEEC data is available for this ${selectedTargetType} yet.`}
          />
        )}
      </section>
    </div>
  );
}
